import React, { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { BaseDialogProps, EditorDialog, showNotification } from 'components';
import { NotificationType } from 'models';
import Yup from 'utils/yup';
import { isAxiosError } from 'utils/errors';
import { RouteNames } from 'config/routeNames';
import { useCreatePermissionGroup } from 'features/permissionGroups';
import LeoTextField from 'components/input-fields/leoTextField/LeoTextField';

import { PermissionGroupErrors } from './PermissionGroupHeader';

export interface CreatePermissionGroupFormValues {
  name: string;
}

export interface PermissionGroupCreateDialogProps extends BaseDialogProps {}

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required().max(255),
});

export interface CreateSubmitHandlerDeps {
  createPermissionGroup: (data: {
    name: string;
    scopes: never[];
    notifications: never[];
  }) => Promise<{ id: ID }>;
  navigate: (path: string) => void;
  t: (key: string) => string;
}

/**
 * Builds the create-dialog's submit handler. Extracted so the collision-detection and
 * navigation logic can be unit tested without mounting the dialog's Formik/MUI tree.
 */
export const createSubmitHandler =
  ({ createPermissionGroup, navigate, t }: CreateSubmitHandlerDeps) =>
  async (values: CreatePermissionGroupFormValues) => {
    try {
      const created = await createPermissionGroup({ name: values.name.trim(), scopes: [], notifications: [] });

      const path = generatePath(RouteNames.KOZOS_JOGOSULTSAGI_CSOPORTOK_RESZLETEK, {
        id: created.id.toString(),
      });

      navigate(`/${path}`);
    } catch (error) {
      if (
        isAxiosError(error) &&
        error.response?.data?.message === PermissionGroupErrors.NAME_ALREADY_EXISTS
      ) {
        showNotification({
          content: t(`ERRORS.${PermissionGroupErrors.NAME_ALREADY_EXISTS}`),
          type: NotificationType.ERROR,
        });
      }

      throw error;
    }
  };

export const PermissionGroupCreateDialog: FC<PermissionGroupCreateDialogProps> = ({
  onClose,
  ...props
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutateAsync: createPermissionGroup } = useCreatePermissionGroup();

  const onSubmit = createSubmitHandler({ createPermissionGroup, navigate, t });

  return (
    <EditorDialog<CreatePermissionGroupFormValues>
      initialValues={{ name: '' }}
      maxWidth="sm"
      onSubmit={onSubmit}
      onClose={onClose}
      validationSchema={validationSchema}
      title={<Trans i18nKey="KOZOS_JOGOSULTSAGI_CSOPORTOK.CREATE_DIALOG_TITLE" />}
      submitText="KOZOS_JOGOSULTSAGI_CSOPORTOK.CREATE_DIALOG_SUBMIT"
      {...props}
    >
      <LeoTextField
        name="name"
        maxLength={255}
        label={<Trans i18nKey="KOZOS_JOGOSULTSAGI_CSOPORTOK.NAME" />}
      />
    </EditorDialog>
  );
};

export default PermissionGroupCreateDialog;
